import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import translations from "../../locals/translations.json";
import { useLanguage } from "../../context/LanguageContext";
import { formatPrice } from "../../utils/functions.js";
import { useAuth } from "../../context/AuthContext";
import { getUserBids } from "../../services/auctionServices.ts";

const MyBids = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const t = translations[language].myBids;

  // State for data and filters
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [activeTab, setActiveTab] = useState("bids"); // "bids", "prebids", "underApproval"
  const [expandedVehicles, setExpandedVehicles] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState(""); // For lot number search
  const [dateFilter, setDateFilter] = useState(""); // For date filter
  const [sortBy, setSortBy] = useState("date"); // "date", "price", "vehicle"
  const [sortOrder, setSortOrder] = useState("desc"); // "asc", "desc"

  // Load all bids data
  const loadAllBids = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError("");

      // Load all bids (contains both live bids and prebids based on isPrebid field)
      const bidsResponse = await getUserBids(user.id);

      let allBids = [];
      if (bidsResponse.success) {
        allBids = bidsResponse.data || bidsResponse.bids || [];
      }

      setBids(allBids);
    } catch (err) {
      console.error("Error loading bids:", err);
      setError("Failed to load bids. Please try again.");
      setBids([]);
    } finally {
      setLoading(false);
    }
  };

  // Toggle vehicle expansion
  const toggleVehicleExpansion = (vehicleId) => {
    setExpandedVehicles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(vehicleId)) {
        newSet.delete(vehicleId);
      } else {
        newSet.add(vehicleId);
      }
      return newSet;
    });
  };

  // Get data to display based on tab
  const getDisplayData = () => {
    if (activeTab === "prebids") {
      // Show only prebids (isPrebid: true)
      return bids.filter((bid) => bid.isPrebid === true) || [];
    } else if (activeTab === "underApproval") {
      // Show bids under approval (isWinningBid: true)
      return bids.filter((bid) => bid.isWinningBid === true) || [];
    } else if (activeTab === "liveBid") {
      // Show only live bids (isPrebid: false or null/undefined)
      return (
        bids.filter(
          (bid) =>
            bid.isPrebid === false ||
            bid.isPrebid === null ||
            bid.isPrebid === undefined
        ) || []
      );
    } else {
      // Bid History - Show ALL bids (both regular bids and prebids)
      return bids || [];
    }
  };

  // Group bids by vehicle for expandable view
  const getGroupedBids = () => {
    const grouped = {};
    const currentData = getDisplayData();

    currentData.forEach((bid) => {
      const vehicleId = bid.vehicleId || bid.vehicle?.id;
      if (!vehicleId) return;

      // Filter by search term (lot number)
      const lotNumber = bid.vehicle?.lotNumber || "";
      if (
        searchTerm &&
        !lotNumber.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return;
      }

      // Filter by date
      if (dateFilter) {
        const bidDate = new Date(bid.createdAt).toISOString().split("T")[0];
        if (bidDate !== dateFilter) {
          return;
        }
      }

      if (!grouped[vehicleId]) {
        grouped[vehicleId] = {
          vehicleId,
          vehicleName: bid.vehicle?.name || "Unknown Vehicle",
          lotNo: bid.vehicle?.lotNumber || "-",
          auctionName: bid.auctionLane?.auction?.name || "-",
          latestBid: bid.vehicle?.latestBid || 0,
          totalBids: 0,
          bids: [],
        };
      }

      // Add bid to the group
      grouped[vehicleId].bids.push(bid);
      grouped[vehicleId].totalBids++;
    });

    // Sort bids within each group by createdAt (latest first)
    Object.values(grouped).forEach((group) => {
      group.bids.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA; // Latest first (descending order)
      });
    });

    // Sort vehicle groups by their latest bid time (latest first)
    const sortedGroups = Object.values(grouped).sort((a, b) => {
      if (a.bids.length === 0 && b.bids.length === 0) return 0;
      if (a.bids.length === 0) return 1; // Put groups with no bids at the end
      if (b.bids.length === 0) return -1; // Put groups with no bids at the end

      const latestBidA = new Date(a.bids[0].createdAt); // First bid is latest due to sorting above
      const latestBidB = new Date(b.bids[0].createdAt); // First bid is latest due to sorting above
      return latestBidB - latestBidA; // Latest first (descending order)
    });

    return sortedGroups;
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setExpandedVehicles(new Set()); // Reset expanded vehicles when changing tabs
  };

  // Handle search
  const handleSearch = () => {
    // The search is already handled by the getGroupedBids function
    // This function can be used for additional search logic if needed
    setExpandedVehicles(new Set()); // Reset expanded vehicles when searching
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchTerm("");
    setDateFilter("");
    setSortBy("date");
    setSortOrder("desc");
    setExpandedVehicles(new Set()); // Reset expanded vehicles when clearing
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Load data on component mount and when user changes
  useEffect(() => {
    loadAllBids();
  }, [user?.id]);

  return (
    <motion.div
      className='bg-white rounded-[8px] sm:rounded-md shadow overflow-x-auto mx-0 sm:mx-1 md:mx-0'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
    >
      {/* Heading */}
      <div className='bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-[8px] sm:rounded-t-[16px] px-1 py-1 border-b border-[#cccccc]'>
        <div className='p-4 sm:p-[20px] text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2'>
          <h2 className='text-sm sm:text-base md:text-lg font-semibold'>
            {t.heading}
          </h2>
        </div>
      </div>

      {/* Top Buttons */}
      <div className='p-2 sm:p-3 md:p-4 lg:p-5'>
        <div className='flex gap-1.5 sm:gap-2 mb-2 sm:mb-3 md:mb-4 overflow-x-auto pb-2 -mx-2 sm:-mx-3 px-2 sm:px-3 scrollbar-hide'>
          <button
            onClick={() => handleTabChange("bids")}
            className={`flex-shrink-0 px-2 sm:px-2.5 md:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[11px] sm:text-xs md:text-sm transition-all duration-200 flex items-center gap-1 sm:gap-1.5 md:gap-2 whitespace-nowrap ${
              activeTab === "bids"
                ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
            }`}
          >
            <span>{t.buttons.bidHistory}</span>
          </button>
          <button
            onClick={() => handleTabChange("liveBid")}
            className={`flex-shrink-0 px-2 sm:px-2.5 md:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[11px] sm:text-xs md:text-sm transition-all duration-200 flex items-center gap-1 sm:gap-1.5 md:gap-2 whitespace-nowrap ${
              activeTab === "liveBid"
                ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
            }`}
          >
            <span>{t.buttons.liveBids}</span>
          </button>
          <button
            onClick={() => handleTabChange("prebids")}
            className={`flex-shrink-0 px-2 sm:px-2.5 md:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[11px] sm:text-xs md:text-sm transition-all duration-200 flex items-center gap-1 sm:gap-1.5 md:gap-2 whitespace-nowrap ${
              activeTab === "prebids"
                ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
            }`}
          >
            <span>{t.buttons.preBid}</span>
          </button>
          <button
            onClick={() => handleTabChange("underApproval")}
            className={`flex-shrink-0 px-2 sm:px-2.5 md:px-3 py-1.5 sm:py-2 rounded-full font-medium text-[11px] sm:text-xs md:text-sm transition-all duration-200 flex items-center gap-1 sm:gap-1.5 md:gap-2 whitespace-nowrap ${
              activeTab === "underApproval"
                ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
            }`}
          >
            <span>{t.buttons.underApproval}</span>
          </button>
        </div>

        {/* Search Filters */}
        <div className='bg-[#F153291F] p-2.5 sm:p-3 md:p-3 lg:p-4 rounded-[8px] sm:rounded-md shadow border mb-2 sm:mb-3 md:mb-4'>
          <div className='space-y-2 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-2 md:gap-2 lg:gap-3 mb-2 sm:mb-3'>
            <input
              type='date'
              placeholder={t.filters.dateFrom}
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className='w-full border border-[#949494] rounded-[8px] sm:rounded-[12px] p-2 sm:p-2 md:p-2.5 lg:p-3 placeholder:text-[10px] sm:placeholder:text-xs md:placeholder:text-xs lg:placeholder:text-sm text-gray-600 text-[11px] sm:text-xs md:text-xs lg:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500'
            />
            <input
              type='text'
              placeholder={t.filters.searchHere}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='w-full border border-[#949494] rounded-[8px] sm:rounded-[12px] p-2 sm:p-2 md:p-2.5 lg:p-3 placeholder:text-[10px] sm:placeholder:text-xs md:placeholder:text-xs lg:placeholder:text-sm text-gray-600 text-[11px] sm:text-xs md:text-xs lg:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500'
            />
          </div>
          <div className='flex flex-col sm:flex-row gap-2 sm:gap-2 md:gap-2 lg:gap-3'>
            <button
              onClick={handleSearch}
              className='w-full sm:w-auto bg-[#F15A29] text-white px-3 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-2 md:py-2.5 lg:py-3 rounded-[8px] sm:rounded hover:bg-[#ff7547] text-[11px] sm:text-xs md:text-xs lg:text-sm font-medium transition-colors'
            >
              {t.buttons.search}
            </button>
            <button
              onClick={handleClearFilters}
              className='w-full sm:w-auto bg-black text-white px-3 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-2 md:py-2.5 lg:py-3 rounded-[8px] sm:rounded hover:bg-gray-800 text-[11px] sm:text-xs md:text-xs lg:text-sm font-medium transition-colors'
            >
              {t.buttons.clear}
            </button>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className='block md:hidden space-y-2.5 sm:space-y-3 md:space-y-4 px-1 sm:px-2'>
          {loading ? (
            <div className='text-center py-6 sm:py-8'>
              <div className='flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-0'>
                <div className='animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-[#F15A29]'></div>
                <span className='ml-0 sm:ml-2 text-xs sm:text-sm'>
                  {activeTab === "prebids"
                    ? "Loading prebids..."
                    : activeTab === "underApproval"
                    ? "Loading bids under approval..."
                    : activeTab === "liveBid"
                    ? "Loading live bids..."
                    : "Loading all bids..."}
                </span>
              </div>
            </div>
          ) : error ? (
            <div className='text-center py-6 sm:py-8 text-red-600 text-xs sm:text-sm px-2'>
              {error}
            </div>
          ) : getGroupedBids().length === 0 ? (
            <div className='text-center py-6 sm:py-8 text-gray-500 text-xs sm:text-sm px-2'>
              {activeTab === "prebids"
                ? "No prebids found"
                : activeTab === "underApproval"
                ? "No bids under approval found"
                : activeTab === "liveBid"
                ? "No live bids found"
                : "No bids found"}
            </div>
          ) : (
            getGroupedBids().map((vehicleGroup) => (
              <div
                key={vehicleGroup.vehicleId}
                className='bg-white rounded-[8px] sm:rounded-lg shadow-md border border-gray-200 overflow-hidden'
              >
                {/* Card Header */}
                <div className='bg-gray-50 px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 border-b border-gray-200'>
                  <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2'>
                    <h3 className='font-semibold text-gray-900 text-xs sm:text-sm md:text-base break-words pr-1'>
                      {vehicleGroup.vehicleName}
                    </h3>
                    <button
                      onClick={() =>
                        toggleVehicleExpansion(vehicleGroup.vehicleId)
                      }
                      className='flex items-center text-[10px] sm:text-xs text-gray-600 hover:text-gray-800 whitespace-nowrap self-start sm:self-auto mt-0.5 sm:mt-0'
                    >
                      <span className='mr-0.5 sm:mr-1'>Your Bid Details</span>
                      <span className='text-xs sm:text-sm'>
                        {expandedVehicles.has(vehicleGroup.vehicleId)
                          ? "▼"
                          : "▶"}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Card Content */}
                <div className='p-2.5 sm:p-3 md:p-4'>
                  {/* Summary Section */}
                  <div className='space-y-1.5 sm:space-y-2 md:space-y-3 mb-2 sm:mb-3 md:mb-4'>
                    <div className='flex justify-between items-center gap-2'>
                      <span className='text-[10px] sm:text-xs md:text-sm text-gray-600 flex-shrink-0'>
                        Lot No:
                      </span>
                      <span className='font-semibold text-red-600 text-[11px] sm:text-xs md:text-sm text-right break-words'>
                        {vehicleGroup.lotNo}
                      </span>
                    </div>
                    <div className='flex justify-between items-center gap-2'>
                      <span className='text-[10px] sm:text-xs md:text-sm text-gray-600 flex-shrink-0'>
                        Latest Bid:
                      </span>
                      <span className='font-semibold text-red-600 text-[11px] sm:text-xs md:text-sm text-right'>
                        {formatPrice(vehicleGroup.latestBid)}
                      </span>
                    </div>
                    <div className='flex justify-between items-center gap-2'>
                      <span className='text-[10px] sm:text-xs md:text-sm text-gray-600 flex-shrink-0'>
                        My Bid:
                      </span>
                      <span className='font-semibold text-red-600 text-[11px] sm:text-xs md:text-sm text-right'>
                        {formatPrice(vehicleGroup.latestBid)}
                      </span>
                    </div>
                  </div>

                  {/* Expandable Bid History Table */}
                  {expandedVehicles.has(vehicleGroup.vehicleId) && (
                    <div className='mb-2 sm:mb-3 md:mb-4 overflow-x-auto -mx-2.5 sm:-mx-3 md:mx-0 px-2.5 sm:px-3 md:px-0'>
                      <table className='w-full border-collapse text-[9px] sm:text-[10px] md:text-xs min-w-[180px] sm:min-w-[200px]'>
                        <thead>
                          <tr className='bg-gray-100'>
                            <th className='text-left py-1 sm:py-1.5 md:py-2 px-1.5 sm:px-2 md:px-3 font-medium text-gray-700 text-[10px] sm:text-xs'>
                              Date/Time
                            </th>
                            <th className='text-right py-1 sm:py-1.5 md:py-2 px-1.5 sm:px-2 md:px-3 font-medium text-gray-700 text-[10px] sm:text-xs'>
                              My Bid
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {vehicleGroup.bids.map((bid, bidIndex) => (
                            <tr
                              key={`${vehicleGroup.vehicleId}-${
                                bid.id || bidIndex
                              }`}
                              className='border-b border-gray-100'
                            >
                              <td className='py-1 sm:py-1.5 md:py-2 px-1.5 sm:px-2 md:px-3 text-gray-600'>
                                {new Date(bid.createdAt).toLocaleTimeString(
                                  "en-US",
                                  {
                                    hour12: false,
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                  }
                                )}
                              </td>
                              <td className='py-1 sm:py-1.5 md:py-2 px-1.5 sm:px-2 md:px-3 text-right font-semibold text-red-600'>
                                {formatPrice(bid.amount || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Footer */}
                  <div className='space-y-1 sm:space-y-1.5 md:space-y-2 pt-2 sm:pt-2.5 border-t border-gray-100'>
                    <div className='flex justify-between items-center gap-2'>
                      <span className='text-[10px] sm:text-xs md:text-sm text-gray-600 flex-shrink-0'>
                        Total Bids:
                      </span>
                      <span className='font-semibold text-[11px] sm:text-xs md:text-sm'>
                        {vehicleGroup.totalBids}
                      </span>
                    </div>
                    <div className='flex justify-between items-start sm:items-center gap-2'>
                      <span className='text-[10px] sm:text-xs md:text-sm text-gray-600 flex-shrink-0'>
                        Auction:
                      </span>
                      <span className='font-semibold text-[11px] sm:text-xs md:text-sm break-words text-right flex-1'>
                        {vehicleGroup.auctionName}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table View */}
        <div className='hidden md:block overflow-x-auto shadow border rounded-[8px] sm:rounded-md'>
          <table className='w-full border-collapse text-xs md:text-xs lg:text-sm xl:text-base min-w-full md:min-w-0 table-auto'>
            <thead>
              <tr className='bg-[#F153291F] text-left'>
                {t.tableHeaders.map((head, i) => (
                  <th
                    key={i}
                    className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-2 md:py-2 lg:py-2.5 xl:py-3 text-xs md:text-xs lg:text-sm whitespace-nowrap font-medium'
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={t.tableHeaders.length}
                    className='text-center py-6 sm:py-8'
                  >
                    <div className='flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-0'>
                      <div className='animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-[#F15A29]'></div>
                      <span className='ml-0 sm:ml-2 text-xs sm:text-sm'>
                        {activeTab === "prebids"
                          ? "Loading prebids..."
                          : activeTab === "underApproval"
                          ? "Loading bids under approval..."
                          : activeTab === "liveBid"
                          ? "Loading live bids..."
                          : "Loading all bids..."}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={t.tableHeaders.length}
                    className='text-center py-6 sm:py-8 text-red-600 text-xs sm:text-sm px-4'
                  >
                    {error}
                  </td>
                </tr>
              ) : getGroupedBids().length === 0 ? (
                <tr>
                  <td
                    colSpan={t.tableHeaders.length}
                    className='text-center py-6 sm:py-8 text-gray-500 text-xs sm:text-sm px-4'
                  >
                    {activeTab === "prebids"
                      ? "No prebids found"
                      : activeTab === "underApproval"
                      ? "No bids under approval found"
                      : activeTab === "liveBid"
                      ? "No live bids found"
                      : "No bids found"}
                  </td>
                </tr>
              ) : (
                getGroupedBids().map((vehicleGroup) => (
                  <React.Fragment key={vehicleGroup.vehicleId}>
                    {/* Main Vehicle Row */}
                    <tr className='border-b hover:bg-gray-50 bg-white transition-colors'>
                      <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-2 md:py-2 lg:py-2.5 xl:py-3 font-medium text-xs md:text-xs lg:text-sm'>
                        <div className='flex items-center gap-1 md:gap-1.5 lg:gap-2'>
                          <button
                            onClick={() =>
                              toggleVehicleExpansion(vehicleGroup.vehicleId)
                            }
                            className='w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 flex items-center justify-center hover:bg-gray-200 rounded text-xs md:text-xs lg:text-sm transition-colors flex-shrink-0'
                          >
                            {expandedVehicles.has(vehicleGroup.vehicleId)
                              ? "▼"
                              : "▶"}
                          </button>
                          <div
                            className='truncate max-w-[80px] md:max-w-[120px] lg:max-w-[180px] xl:max-w-[250px] 2xl:max-w-none'
                            title={vehicleGroup.vehicleName}
                          >
                            {vehicleGroup.vehicleName}
                          </div>
                        </div>
                      </td>
                      <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-2 md:py-2 lg:py-2.5 xl:py-3 font-medium text-center text-xs md:text-xs lg:text-sm'>
                        {vehicleGroup.lotNo}
                      </td>
                      <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-2 md:py-2 lg:py-2.5 xl:py-3 font-medium text-center text-xs md:text-xs lg:text-sm'>
                        {vehicleGroup.totalBids}
                      </td>
                      <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-2 md:py-2 lg:py-2.5 xl:py-3 text-center text-xs md:text-xs lg:text-sm'>
                        <div
                          className='truncate max-w-[60px] md:max-w-[100px] lg:max-w-[140px] xl:max-w-[200px] 2xl:max-w-none mx-auto'
                          title={vehicleGroup.auctionName}
                        >
                          {vehicleGroup.auctionName}
                        </div>
                      </td>
                      <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-2 md:py-2 lg:py-2.5 xl:py-3 font-semibold text-orange-600 text-center text-xs md:text-xs lg:text-sm whitespace-nowrap'>
                        {formatPrice(vehicleGroup.latestBid)}
                      </td>
                      <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-2 md:py-2 lg:py-2.5 xl:py-3 font-semibold text-green-600 text-center text-xs md:text-xs lg:text-sm'>
                        -
                      </td>
                      <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-2 md:py-2 lg:py-2.5 xl:py-3 text-center text-xs md:text-xs lg:text-sm whitespace-nowrap'>
                        {formatDate(vehicleGroup.bids[0]?.createdAt)}
                      </td>
                    </tr>

                    {/* Expandable Bid Details */}
                    {expandedVehicles.has(vehicleGroup.vehicleId) &&
                      vehicleGroup.bids.map((bid, bidIndex) => (
                        <tr
                          key={`${vehicleGroup.vehicleId}-${
                            bid.id || bidIndex
                          }`}
                          className='border-b hover:bg-gray-25 bg-gray-50 transition-colors'
                        >
                          <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-1.5 md:py-1.5 lg:py-2 xl:py-2.5 pl-5 md:pl-6 lg:pl-8 xl:pl-10 text-xs md:text-xs lg:text-sm text-gray-600'>
                            {/* {bid.isPrebid === true ? (
                              <span className='px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full inline-flex items-center gap-1'>
                                <span className='text-[8px]'>🔵</span> Prebid
                              </span>
                            ) : (
                              <span className='px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium inline-flex items-center gap-1'>
                                <span className='text-[8px]'>🟢</span> Live Bid
                              </span>
                            )} */}
                          </td>

                          <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-1.5 md:py-1.5 lg:py-2 xl:py-2.5 text-center text-xs md:text-xs lg:text-sm text-gray-600'></td>
                          <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-1.5 md:py-1.5 lg:py-2 xl:py-2.5 text-center text-xs md:text-xs lg:text-sm text-gray-600'></td>
                          <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-1.5 md:py-1.5 lg:py-2 xl:py-2.5 text-center text-xs md:text-xs lg:text-sm text-gray-600'></td>
                          <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-1.5 md:py-1.5 lg:py-2 xl:py-2.5 text-center text-xs md:text-xs lg:text-sm text-gray-600'></td>
                          <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-1.5 md:py-1.5 lg:py-2 xl:py-2.5 font-semibold text-green-600 text-center text-xs md:text-xs lg:text-sm whitespace-nowrap'>
                            {bidIndex === 0
                              ? formatPrice(vehicleGroup.latestBid)
                              : formatPrice(bid.amount || 0)}
                          </td>
                          <td className='px-2 md:px-2.5 lg:px-3 xl:px-4 py-1.5 md:py-1.5 lg:py-2 xl:py-2.5 text-center text-xs md:text-xs lg:text-sm text-gray-600 whitespace-nowrap'>
                            {new Date(bid.createdAt).toLocaleTimeString(
                              "en-US",
                              {
                                hour12: false,
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              }
                            )}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

export default MyBids;
